import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import SEOHead from '../SEO/SEOHead';
import ImageSlider from './ImageSlider';
import WhyDonateSection from './WhyDonateSection';
import VideoSection from './VideoSection';
import StatsCounter from './StatsCounter';
import BloodTypesSection from './BloodTypesSection';
import EmergencySection from './EmergencySection';
import GallerySection from './GallerySection';
import FeaturedProjects from '../homepage/FeaturedProjects';
import { generateStructuredData } from '../../utils/sitemapGenerator';

export default function HomePage() {
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [locationStatus, setLocationStatus] = useState('prompt'); // prompt, granted, denied

  const requestLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Success
          console.log("Location permission granted");
          setLocationStatus('granted');
          setShowLocationPrompt(false);
        },
        (error) => {
          // Error
          console.error("Error getting location:", error);
          setLocationStatus('denied');
          setShowLocationPrompt(true);
        }
      );
    } else {
      console.log("Geolocation not supported");
    }
  };

  // Request geolocation on mount
  useEffect(() => {
    requestLocation();
  }, []);

  // Generate structured data for the homepage
  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BloodHero - Blood Donation Platform | Connect Donors & Save Lives",
    "description": "BloodHero is a comprehensive blood donation platform connecting donors with those in need. Emergency blood requests, NGO projects, vehicle booking, and 24/7 support. Save lives today!",
    "url": "https://www.thebloodhero.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.thebloodhero.com/search-requests?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "mainEntity": {
      "@type": "Organization",
      "name": "BloodHero",
      "description": "Blood donation platform connecting donors with those in need",
      "url": "https://www.thebloodhero.com",
      "logo": "https://www.thebloodhero.com/images/logo1.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-9992396623",
        "contactType": "customer service",
        "availableLanguage": "English, Hindi",
        "areaServed": "IN",
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday"
          ],
          "opens": "00:00",
          "closes": "23:59"
        }
      },
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "IN",
        "addressRegion": "Haryana",
        "addressLocality": "Yamuna Nagar"
      },
      "sameAs": [
        "https://www.facebook.com/bloodhero",
        "https://www.twitter.com/bloodhero",
        "https://www.instagram.com/bloodhero",
        "https://www.linkedin.com/company/bloodhero"
      ]
    }
  };

  return (
    <>
      <SEOHead
        title="Blood Donation Platform India | Emergency Blood Request | BloodHero"
        description="Blood donation platform India - Connect blood donors with emergency blood requests. 24/7 blood donation service, NGO projects, and blood bank coordination. Save lives through blood donation today!"
        keywords="blood donation, blood donor, emergency blood request, blood donation platform India, blood bank, NGO blood donation, save lives, blood donation Haryana, emergency blood donation, blood donation service"
        canonicalUrl="/"
        ogImage="/images/blood-hero-homepage-og.jpg"
        structuredData={homepageStructuredData}
      />

      {/* Persistent Location Request Modal */}
      {showLocationPrompt && locationStatus === 'denied' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-bounce-in relative">
            <button
              onClick={() => setShowLocationPrompt(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-red-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">Location Required</h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                To find the nearest blood donors and emergency requests, we need access to your location.
                Please enable location permission in your browser settings and try again.
              </p>

              <button
                onClick={requestLocation}
                className="w-full bg-red-600 text-white rounded-xl py-3 font-semibold hover:bg-red-700 transition-all shadow-lg shadow-red-200"
              >
                Allow Location Access
              </button>
            </div>
          </div>
        </div>
      )}

      <main role="main" itemScope itemType="https://schema.org/WebPage">
        <meta itemProp="name" content="BloodHero - Blood Donation Platform" />

        {/* Main H1 Heading for SEO */}
        <section className="sr-only">
          <h1>Blood Donation Platform India | Emergency Blood Request | BloodHero</h1>
        </section>

        {/* Hero Section with Enhanced Content */}
        <ImageSlider />
        <WhyDonateSection />
        <StatsCounter />
        <BloodTypesSection />
        <EmergencySection />
        <FeaturedProjects />
        <GallerySection />
        <VideoSection />
      </main>
    </>
  );
}
